import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  serverTimestamp,
  increment,
} from 'firebase/firestore';
import { db } from '../firebase';
import { createNotification, markNotificationsAsReadForBounty } from './useNotifications';

/**
 * Hook for fetching all claims on a specific bounty (for owner).
 * Requires creatorUid so that the Firestore security rule
 * (isOwner(resource.data.bountyCreatorUid)) can be satisfied by the query.
 */
export const useBountyClaims = (bountyId, creatorUid) => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!bountyId || !creatorUid) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'claims'),
      where('bountyCreatorUid', '==', creatorUid),
      where('bountyId', '==', bountyId)
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setClaims(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching bounty claims:', err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [bountyId]);

  return { claims, loading };
};

/**
 * Hook for fetching a single claim by ID
 */
export const useClaim = (claimId) => {
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!claimId) return;

    const unsubscribe = onSnapshot(
      doc(db, 'claims', claimId),
      (snap) => {
        setClaim(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setLoading(false);
      },
      () => setLoading(false)
    );

    return unsubscribe;
  }, [claimId]);

  return { claim, loading };
};

/**
 * Hook for fetching claims submitted by the current user
 */
export const useMyClaims = (uid) => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'claims'),
      where('claimantUid', '==', uid),
      orderBy('submittedAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setClaims(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching my claims:', err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [uid]);

  return { claims, loading };
};

/**
 * Hook for fetching approved claims by a specific user (for public profile)
 */
export const useUserApprovedClaims = (userId) => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'claims'),
      where('claimantUid', '==', userId),
      where('status', '==', 'approved')
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const sorted = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.reviewedAt?.seconds || 0) - (a.reviewedAt?.seconds || 0));
        setClaims(sorted);
        setLoading(false);
      },
      () => setLoading(false)
    );

    return unsubscribe;
  }, [userId]);

  return { claims, loading };
};

/**
 * Create a new claim and update the bounty
 */
export const createClaim = async (data, bountyId, uid, anonymizedUsername, claimantName) => {
  const bountySnap = await getDoc(doc(db, 'bounties', bountyId));
  if (!bountySnap.exists()) throw new Error('Bounty not found');
  const bounty = bountySnap.data();

  const claimRef = await addDoc(collection(db, 'claims'), {
    bountyId,
    bountyTitle: bounty.title,
    bountyCreatorUid: bounty.creatorUid,
    bountyThumbnailUrl: bounty.thumbnailUrl || null,
    bountyResearchType: bounty.researchType || null,
    claimantUid: uid,
    claimantAnonymizedUsername: anonymizedUsername,
    claimantName: claimantName || null,
    researchFindings: data.researchFindings,
    attachmentUrls: data.attachmentUrls || [],
    sources: data.sources || [],
    status: 'pending',
    submittedAt: serverTimestamp(),
    reviewedAt: null,
    collectorRating: null,
    collectorFeedback: '',
    disputeReason: null,
  });

  await updateDoc(doc(db, 'bounties', bountyId), {
    claimCount: increment(1),
    status: 'in_review',
    updatedAt: serverTimestamp(),
  });

  // Notify bounty creator of new claim (non-blocking)
  try {
    await createNotification(
      bounty.creatorUid,
      'claim_submitted',
      'New Claim Submitted',
      `${claimantName || anonymizedUsername} submitted research for your bounty.`,
      bountyId,
      bounty.title,
      claimRef.id,
      `/tasks/${bountyId}/review`
    );
  } catch (err) {
    console.error('Failed to send notification:', err);
  }

  return claimRef.id;
};

/**
 * Approve a claim, mark the bounty as completed, and update researcher reputation
 */
export const approveClaim = async (claimId, bountyId, rating, feedback) => {
  const claimSnap = await getDoc(doc(db, 'claims', claimId));
  const claimData = claimSnap.data();

  await updateDoc(doc(db, 'claims', claimId), {
    status: 'approved',
    collectorRating: rating,
    collectorFeedback: feedback || '',
    reviewedAt: serverTimestamp(),
  });

  // Mark claim_submitted notifications as read for this bounty
  if (claimData?.bountyCreatorUid) {
    await markNotificationsAsReadForBounty(claimData.bountyCreatorUid, bountyId);
  }

  await updateDoc(doc(db, 'bounties', bountyId), {
    status: 'completed',
    acceptedClaimId: claimId,
    completedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Update researcher's reputation
  if (claimData?.claimantUid) {
    const researcherRef = doc(db, 'users', claimData.claimantUid);
    const researcherSnap = await getDoc(researcherRef);
    if (researcherSnap.exists()) {
      const rep = researcherSnap.data().reputation || { rating: 0, totalRatings: 0, completedBounties: 0 };
      const newTotalRatings = (rep.totalRatings || 0) + 1;
      const newRating = ((rep.rating || 0) * (rep.totalRatings || 0) + rating) / newTotalRatings;
      await updateDoc(researcherRef, {
        'reputation.rating': newRating,
        'reputation.totalRatings': newTotalRatings,
        'reputation.completedBounties': (rep.completedBounties || 0) + 1,
      });
    }
  }

  // Create transaction record
  try {
    const bountySnap = await getDoc(doc(db, 'bounties', bountyId));
    const bountyData = bountySnap.data();
    await addDoc(collection(db, 'transactions'), {
      fromUid: claimData.bountyCreatorUid,
      toUid: claimData.claimantUid,
      amount: bountyData?.rewardAmount || 0,
      bountyId,
      bountyTitle: claimData.bountyTitle,
      claimId,
      claimantUsername: claimData.claimantAnonymizedUsername,
      collectorUsername: bountyData?.creatorAnonymizedUsername || '',
      status: 'completed',
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    console.error('Failed to create transaction:', err);
  }

  // Notify researcher of approval (non-blocking)
  try {
    await createNotification(
      claimData.claimantUid,
      'claim_approved',
      'Claim Approved!',
      `Your research on "${claimData.bountyTitle}" was approved! You earned ${rating} star${rating !== 1 ? 's' : ''}.`,
      bountyId,
      claimData.bountyTitle,
      claimId,
      `/submissions/${claimId}`
    );
  } catch (err) {
    console.error('Failed to send notification:', err);
  }
};

/**
 * Reject a claim (used by admin during arbitration resolution)
 */
export const rejectClaim = async (claimId) => {
  const claimSnap = await getDoc(doc(db, 'claims', claimId));
  const claimData = claimSnap.data();

  await updateDoc(doc(db, 'claims', claimId), {
    status: 'rejected',
    reviewedAt: serverTimestamp(),
  });

  // Notify researcher of rejection (non-blocking)
  if (claimData) {
    try {
      await createNotification(
        claimData.claimantUid,
        'claim_rejected',
        'Claim Rejected',
        `Your research on "${claimData.bountyTitle}" was not accepted.`,
        claimData.bountyId,
        claimData.bountyTitle,
        claimId,
        `/submissions/${claimId}`
      );
    } catch (err) {
      console.error('Failed to send notification:', err);
    }
  }
};

/**
 * Dispute a claim — updates claim to 'disputed' and opens an arbitration case
 */
export const disputeClaim = async (claimId, reason, collectorUid) => {
  const claimSnap = await getDoc(doc(db, 'claims', claimId));
  const claimData = claimSnap.data();

  await updateDoc(doc(db, 'claims', claimId), {
    status: 'disputed',
    disputeReason: reason,
    reviewedAt: serverTimestamp(),
  });

  // Mark claim_submitted notifications as read for this bounty
  await markNotificationsAsReadForBounty(collectorUid, claimData.bountyId);

  await addDoc(collection(db, 'arbitration'), {
    claimId,
    bountyId: claimData.bountyId,
    collectorUid,
    researcherUid: claimData.claimantUid,
    reason,
    status: 'open',
    initiatedAt: serverTimestamp(),
  });

  // Notify researcher of dispute (non-blocking)
  try {
    await createNotification(
      claimData.claimantUid,
      'claim_disputed',
      'Claim Disputed',
      `Your claim on "${claimData.bountyTitle}" has been disputed. Our team will review.`,
      claimData.bountyId,
      claimData.bountyTitle,
      claimId,
      `/submissions/${claimId}`
    );
  } catch (err) {
    console.error('Failed to send notification:', err);
  }
};
